import { useState } from "react";
import { apiPost } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Sparkles, Info, Lightbulb, TrendingUp, ChevronRight } from "lucide-react";

/**
 * Wraps any chart with (1) a plain-language narration caption and
 * (2) a "Deep dive & AI guidance" drawer that fetches AI interpretation + optimisation tips.
 *
 * Props: title, context (short static narration), app, data (array), children (the chart)
 */
export function ChartInsight({ title, context, app = "", data = [], children }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [insight, setInsight] = useState(null);

  const openDrawer = async () => {
    setOpen(true);
    if (insight) return;
    setLoading(true);
    try {
      const r = await apiPost("/ai/chart-insight", { chart: title, context, app, data });
      setInsight(r);
    } catch (e) {
      setInsight({ narration: context, observations: [], recommendations: [] });
    }
    setLoading(false);
  };

  return (
    <div data-testid="chart-insight">
      {children}
      {context && <p className="text-[11px] text-muted-foreground mt-2 leading-snug flex items-start gap-1.5"><Info className="h-3.5 w-3.5 mt-px shrink-0 text-navy" />{context}</p>}
      <button onClick={openDrawer} data-testid={`insight-btn-${(title || "chart").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-navy hover:text-gold transition-colors">
        <Sparkles className="h-3.5 w-3.5" />Deep dive &amp; AI guidance<ChevronRight className="h-3.5 w-3.5" />
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="insight-drawer">
          <SheetHeader>
            <SheetTitle className="font-display flex items-center gap-2"><TrendingUp className="h-4 w-4 text-navy" />{title}</SheetTitle>
          </SheetHeader>

          {loading ? (
            <div className="space-y-3 mt-5">
              <Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-5/6" /><Skeleton className="h-24 w-full" /><Skeleton className="h-24 w-full" />
            </div>
          ) : insight ? (
            <div className="space-y-5 mt-5">
              <section>
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5 flex items-center gap-1.5"><Info className="h-3.5 w-3.5" />What this means</p>
                <p className="text-sm leading-6" data-testid="insight-narration">{insight.narration}</p>
              </section>

              {data?.length > 0 && (
                <section>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">Underlying data</p>
                  <div className="rounded-lg border divide-y max-h-52 overflow-y-auto">
                    {data.slice(0, 12).map((d, i) => (
                      <div key={i} className="flex items-center justify-between px-3 py-1.5 text-sm">
                        <span className="text-muted-foreground truncate">{d.label ?? d.date ?? `Item ${i + 1}`}</span>
                        <span className="font-mono-nav font-medium">{typeof (d.usd ?? d.value) === "number" ? (d.usd ?? d.value).toLocaleString(undefined, { maximumFractionDigits: 0 }) : (d.usd ?? d.value ?? "—")}</span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {insight.observations?.length > 0 && (
                <section>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5 flex items-center gap-1.5"><TrendingUp className="h-3.5 w-3.5" />Key observations</p>
                  <ul className="space-y-1.5">
                    {insight.observations.map((o, i) => (
                      <li key={i} className="text-sm flex items-start gap-2"><span className="h-1.5 w-1.5 rounded-full bg-navy mt-1.5 shrink-0" />{o}</li>
                    ))}
                  </ul>
                </section>
              )}

              {insight.recommendations?.length > 0 && (
                <section className="rounded-xl bg-[rgba(63,191,143,0.08)] border border-[rgba(63,191,143,0.25)] p-3">
                  <p className="text-[11px] uppercase tracking-wide text-[hsl(158_50%_38%)] mb-2 flex items-center gap-1.5 font-semibold"><Lightbulb className="h-3.5 w-3.5" />AI guidance — how to improve</p>
                  <ul className="space-y-2" data-testid="insight-recommendations">
                    {insight.recommendations.map((r, i) => (
                      <li key={i} className="text-sm flex items-start gap-2"><span className="h-4 w-4 rounded-full bg-navy text-white text-[9px] font-bold flex items-center justify-center mt-0.5 shrink-0">{i + 1}</span>{r}</li>
                    ))}
                  </ul>
                </section>
              )}
              <p className="text-[10px] text-muted-foreground">Generated by Navitra Lens AI. Validate before acting on financial decisions.</p>
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}
